import { Helmet } from 'react-helmet-async';
import { type FC, type MouseEvent, useEffect, useState, useCallback } from 'react';
import { Box, Button, Card, Stack, LinearProgress, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import {
	useLazyGetRequestQuery,
	usePayRequestMutation,
	type GetRequestsResponse,
	useGetRequestReportsQuery,
	useLazyGetRequestReportQuery,
	useUploadRequestReportMutation,
	useDeleteRequestReportMutation,
	useSetRequestAdminNoteMutation,
	useSetRequestAccountNoteMutation,
	useGetRequestPaymentSummaryQuery,
} from '../../../slices/apis/requestApi';

import Iconify from '../../../components/iconify';
import SuccessAlert from '../../../components/alert';
import Toast from '../../../components/toast';
import InfoCard from '../../../components/info-card';
import {
	Note,
	TestReview,
	Candidate,
	Invoices,
	AssessmentDetail,
	RequestReports,
	TotalSummary,
} from '../../../components/shared';
import { Status } from '../../../components/request';
import { Test, BulkRequest } from '../../../models';
import { getFileMime } from '../../../utils/config';
import { omit } from '../../../utils/helpers';
import { fDateTime } from '../../../utils/formatTime';
import { useRole } from '../../../hooks/useRole';
import { useRequestRules } from '../../../hooks/useRequestRules';

interface RequestViewProps {
	backwardRoute: string;
}

const RequestView: FC<RequestViewProps> = ({ backwardRoute }) => {
	const { isAdmin, isSupplier } = useRole();
	const { reqId } = useParams() as { reqId: string };
	const [searchParam] = useSearchParams();
	const navigate = useNavigate();
	const { allowPayment } = useRequestRules(reqId ?? '');
	const [paymentSuccessAlertOpen, setPaymentSuccessAlertOpen] = useState(false);

	const [payRequest, { isSuccess, data }] = usePayRequestMutation();
	const [setRequestAdminNote, { isSuccess: isUpdateAdminNoteSuccess, isError: isUpdateAdminNoteError }] =
		useSetRequestAdminNoteMutation();
	const [setRequestAccountNote, { isSuccess: isUpdateAccountNoteSuccess, isError: isUpdateAccountNoteError }] =
		useSetRequestAccountNoteMutation();
	const [trigger, result] = useLazyGetRequestQuery();

	const { data: paymentSummary } = useGetRequestPaymentSummaryQuery(reqId);

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	const handleSuccessAlertClose = useCallback(() => {
		setPaymentSuccessAlertOpen(false);
	}, []);

	const handleAdminNoteSave = (note: string) => {
		setRequestAdminNote({
			id: reqId,
			note,
		});
	};

	const handleAccountNoteSave = (note: string) => {
		setRequestAccountNote({
			id: reqId,
			note,
		});
	};

	useEffect(() => {
		if (isSuccess && data) {
			window.location.href = data.url;
		}
	}, [isSuccess, data]);

	useEffect(() => {
		let timer: NodeJS.Timeout;

		if (searchParam.get('from') === 'payment_success') {
			timer = setTimeout(() => {
				console.log('lazy load after 5 seconds');
				setPaymentSuccessAlertOpen(true);
				trigger(reqId);
			}, 5000);
		} else {
			trigger(reqId);
		}

		return () => clearTimeout(timer);
	}, [reqId, trigger, searchParam]);

	const onPayRequest = () => {
		payRequest({
			requestId: request.id,
			items: tests.map((test) => ({ id: test.id })),
		});
	};

	if (result.isLoading || !result.data || !paymentSummary) {
		return <LinearProgress />;
	}

	const request = result.data as GetRequestsResponse;
	const { tests } = request;

	const scheduleDateLineText = BulkRequest.isAssessmentDatesSet(request.assessmentDates) ? 'Scheduled' : 'Preferred';
	const scheduleDates = BulkRequest.isAssessmentDatesSet(request.assessmentDates)
		? request.assessmentDates
		: request.prefAssessmentDates;

	return (
		<>
			<Helmet>
				<title> Request Detail - {process.env.REACT_APP_ORG} </title>
			</Helmet>

			{isUpdateAdminNoteSuccess && (
				<Toast body={'Admin note has been updated successfully!'} severity="success" autoHideDuration={3000} />
			)}

			{isUpdateAdminNoteError && (
				<Toast body={'Admin note failed to update!'} severity="error" autoHideDuration={3000} />
			)}

			{isUpdateAccountNoteSuccess && (
				<Toast body={'Account note has been updated successfully!'} severity="success" autoHideDuration={3000} />
			)}

			{isUpdateAccountNoteError && (
				<Toast body={'Account note failed to update!'} severity="error" autoHideDuration={3000} />
			)}

			<SuccessAlert
				open={paymentSuccessAlertOpen}
				title={'Thank you for your payment!'}
				subtitle={
					'Note your appointment is NOT CONFIRMED until you receive a confirmation email with your scheduled time.'
				}
				onClose={handleSuccessAlertClose}
			/>

			<Box sx={{ pl: 2, pr: 2 }}>
				<Stack spacing={2} alignItems="flex-start">
					<Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
						<Typography variant="h5">Request #{request?.id}</Typography>
						{allowPayment && (
							<Button disabled={isSuccess} onClick={onPayRequest} color="brand" variant="contained">
								<span>Pay Now</span>
							</Button>
						)}
						<Button variant="outlined" color="error" data-route={backwardRoute} onClick={handleNavigate}>
							Back
						</Button>
					</Box>
					<Status status={request.status} />
				</Stack>
			</Box>

			<Box sx={{ p: 2 }}>
				<Grid container spacing={3}>
					<Grid size={{ xs: 12, md: 6 }}>
						<Stack spacing={3}>
							<InfoCard title="Assessment Details">
								<AssessmentDetail
									assessmentDates={scheduleDates}
									location={request.supplier.address}
									dateLineText={scheduleDateLineText}
								/>
								{request.status.toLowerCase() === 'cancelled' && (
									<Stack direction="row" spacing={0.5} alignItems="center">
										<Iconify icon="eva:alert-triangle-outline" sx={{ color: 'error.main' }} />
										<Typography color="error">
											This request has been cancelled at {fDateTime(request.updatedAt)}
										</Typography>
									</Stack>
								)}
							</InfoCard>
							<Candidate candidate={result.data.candidate} requestExtraInfo={result.data.extraInfo} />
							<TestReview
								tests={tests.map((test) => {
									const normalised = Test.normalise(test);

									if (isSupplier) {
										return omit(normalised, ['price']);
									}

									return normalised;
								})}
							/>
						</Stack>
					</Grid>
					<Grid size={{ xs: 12, md: 6 }}>
						<Stack spacing={3}>
							{!isSupplier && paymentSummary.paymentStatus.toLowerCase() === 'fully_paid' && (
								<TotalSummary
									total={paymentSummary.paidAmount}
									subtotal={paymentSummary.totalWithoutGst}
									gst={paymentSummary.paidGstAmount}
									discount={paymentSummary.discountedAmount}
								/>
							)}
							{!isSupplier && paymentSummary.transactions.length > 0 && (
								<Invoices transactions={paymentSummary.transactions} />
							)}
							{request.id && (
								<Card variant="outlined">
									<RequestReports
										requestId={Number(request.id)}
										apis={{
											useGetRequestReportsQuery,
											useLazyGetRequestReportQuery,
											useUploadRequestReportMutation,
											useDeleteRequestReportMutation,
										}}
										mimeTypes={getFileMime()}
									/>
								</Card>
							)}
							{isAdmin && <Note title="Admin Note" val={result.data.adminNote} onSave={handleAdminNoteSave} />}
							{/* used by internal account team who're also assigned admin roles */}
							{isAdmin && <Note title="Account Note" val={result.data.accountNote} onSave={handleAccountNoteSave} />}
						</Stack>
					</Grid>
				</Grid>
			</Box>
		</>
	);
};

export default RequestView;
